/**
 * Input sanitization and edit-display helpers for EV/BV financial fields.
 *
 * @module coverageInfo/helpers/evCoverageFinancialInput
 */

const NA_LITERAL = "NA";

/**
 * Keeps only characters that form a numeric literal (optional single decimal).
 *
 * @param raw - Uppercased or mixed-case input fragment
 * @returns Sanitized numeric string
 */
const sanitizeNumericOnly = (raw: string): string => {
  let result = "";
  let hasDot = false;
  for (const ch of raw) {
    if (ch >= "0" && ch <= "9") {
      result += ch;
    } else if (ch === "." && !hasDot) {
      result += ".";
      hasDot = true;
    }
  }
  return result;
};

/**
 * Restricts BV financial text inputs to partial or complete `NA` or numeric values.
 *
 * @param raw - Raw text from an editable `LabeledValue`
 * @returns Sanitized value safe to store in local edit state
 */
export const sanitizeNaOrNumericInput = (raw: string): string => {
  const upper = raw.toUpperCase();
  if (upper === "") {
    return "";
  }

  const filtered = upper.replace(/[^NA0-9.]/g, "");
  if (/[0-9.]/.test(filtered)) {
    return sanitizeNumericOnly(filtered);
  }

  let result = "";
  for (const ch of filtered) {
    if (ch !== "N" && ch !== "A") {
      continue;
    }
    const candidate = result + ch;
    if (NA_LITERAL.startsWith(candidate)) {
      result = candidate;
    }
  }
  return result;
};

/**
 * Formats a deductible/OOP numeric field for edit mode (empty when unset).
 *
 * @param value - Parsed amount from the coverage model
 * @returns Plain numeric string, or empty when unset so the user can type freely
 */
export const amountEditValue = (value: number | undefined): string => {
  if (value === undefined || Number.isNaN(value)) {
    return "";
  }
  return String(value);
};

/**
 * Returns the NA-prefix slice of a string when it is only `N` / `A` characters.
 *
 * @param raw - Trimmed miscellaneous amount text
 * @returns Prefix of `NA` being typed, or empty when not in NA mode
 */
const naPrefixEditSlice = (raw: string): string => {
  const upper = raw.toUpperCase();
  if (/[0-9.]/.test(upper)) {
    return "";
  }
  let result = "";
  for (const ch of upper) {
    if (ch !== "N" && ch !== "A") {
      continue;
    }
    const candidate = result + ch;
    if (NA_LITERAL.startsWith(candidate)) {
      result = candidate;
    }
  }
  return result;
};

/**
 * Strips display formatting from copay / co-insurance for edit mode.
 *
 * @param amount - Stored miscellaneous amount string (may include `$` or `%`)
 * @returns Plain numeric string, `NA` while editing NA, or empty when unset
 */
export const miscAmountEditValue = (amount: string | undefined): string => {
  const trimmed = (amount ?? "").trim();
  if (trimmed === "" || trimmed === "—") {
    return "";
  }

  const naPrefix = naPrefixEditSlice(trimmed);
  if (naPrefix !== "") {
    return naPrefix;
  }

  const stripped = trimmed.replace(/[$%,]/g, "").trim();
  if (stripped === "") {
    return "";
  }

  if (/^[0-9]*\.?[0-9]*$/.test(stripped) && stripped !== "") {
    return stripped;
  }

  const parsed = Number.parseFloat(stripped);
  if (!Number.isFinite(parsed)) {
    return trimmed;
  }
  return String(parsed);
};

/**
 * Edit-mode display for deductible/OOP fields (prefers in-progress raw text).
 *
 * When the raw value is already a clean numeric-or-NA string (no `$`, `%`,
 * commas, or dashes) it is returned verbatim so trailing decimals like
 * `"90."` survive re-render and the user can keep typing `"90.99"`.
 * Formatted API values (e.g. `"$5,800"`) still go through
 * {@link miscAmountEditValue} to strip currency symbols.
 *
 * @param raw - User- or payer-supplied text (`N`, `NA`, `-`, etc.)
 * @param parsed - Numeric value used for validation when the field is numeric
 * @returns Text shown in the editable `LabeledValue`
 */
export const bvAmountFieldEditValue = (
  raw: string | undefined,
  parsed: number | undefined,
): string => {
  const rawTrimmed = raw?.trim() ?? "";
  if (rawTrimmed !== "") {
    if (/^[NA0-9.]+$/i.test(rawTrimmed)) {
      return rawTrimmed;
    }
    return miscAmountEditValue(raw);
  }
  return amountEditValue(parsed);
};

/**
 * Wraps a change handler so only `NA` or numeric text is persisted.
 *
 * @param handler - Callback that receives the sanitized value
 * @returns `LabeledValue` `onValueChange` handler
 */
export const bindNaOrNumericValueChange =
  (handler: (value: string) => void) =>
  (raw: string): void => {
    handler(sanitizeNaOrNumericInput(raw));
  };
